import { useState } from "react";

export default function ConvertOptionsModal({
  paletteBrand,
  customPalette,
  pendingDominantColorCount,
  onPendingDominantColorCountChange,
  onAnalyzeSelectedColors,
  onConvertToStitches,
  onUseOverlay,
  cartoonifyEnabled = false,
  onCartoonifyChange,
  fractionalDetailEnabled = false,
  onFractionalDetailChange,
  fractionalIntensityValue = 3,
  fractionalIntensityOptions = [],
  onFractionalIntensityChange,
  cols,
  rows,
  isPro,
  onClose,
}) {
  const [selectedConversionBrand, setSelectedConversionBrand] =
    useState(paletteBrand);

  const sliderMax = Math.max(0, fractionalIntensityOptions.length - 1);
  const safeFractionalValue = Math.min(
    sliderMax,
    Math.max(0, fractionalIntensityValue)
  );

  const isPaletteEmpty = customPalette.length === 0;
  const isProFeatureDisabled = !isPro;

  return (
    <div className="modal-backdrop">
      <div className="modal settings-modal">
        <div className="modal-header">
          <h2>Image Import Options</h2>
          <button className="icon-btn" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modal-body">
          <h3>How do you want to use this image?</h3>
          <p>
            You can use the image as a semi-transparent guide under your
            pattern, or automatically convert it into stitches using your
            current palette.
          </p>

          <hr />

          <div className="setting-group">
            <label htmlFor="conversion-palette">
              Select Thread Palette for Analysis:
            </label>
            <select
              id="conversion-palette"
              value={selectedConversionBrand}
              onChange={(event) =>
                setSelectedConversionBrand(event.target.value)
              }
              className="select"
              style={{ marginBottom: "10px" }}
            >
              <option value="DMC">DMC Threads</option>
              <option value="Anchor">Anchor Threads</option>
            </select>

            <label htmlFor="dominant-color-count">
              Number of closest {selectedConversionBrand} colors to extract:
            </label>
            <input
              id="dominant-color-count"
              type="number"
              value={pendingDominantColorCount}
              onChange={(event) =>
                onPendingDominantColorCountChange(event.target.value)
              }
              min="1"
              placeholder="100"
              className="input"
              style={{ marginBottom: "10px" }}
            />
            <button
              className="btn btn-primary"
              onClick={() =>
                onAnalyzeSelectedColors(
                  selectedConversionBrand,
                  pendingDominantColorCount
                )
              }
              style={{ width: "100%", marginBottom: "10px" }}
            >
              Analyze & Auto-Select Top {pendingDominantColorCount} closest{" "}
              {selectedConversionBrand} Colors
            </button>
            <p className="hint">
              Analyzes the image and adds the top {pendingDominantColorCount}{" "}
              closest colors from the {selectedConversionBrand} palette to your
              My Palette automatically. Use this first if your palette is empty.
            </p>
          </div>

          <hr />

          <div className="setting-group">
            <label
              className="checkbox"
              style={{ display: "flex", gap: "8px", alignItems: "center" }}
            >
              <input
                type="checkbox"
                checked={cartoonifyEnabled}
                onChange={(event) =>
                  onCartoonifyChange?.(event.target.checked)
                }
              />
              Cartoonify image before analysis/conversion (beta)
            </label>
            <p className="hint">
              Posterizes colors and adds ink-style edges so photos behave more
              like illustrated artwork. Great for reducing muddy color pools
              (e.g., the yellow tint on the wolf photo).
            </p>
          </div>

          <div className="setting-group">
            <label
              className="checkbox"
              style={{ display: "flex", gap: "8px", alignItems: "center" }}
            >
              <input
                type="checkbox"
                checked={fractionalDetailEnabled}
                onChange={(event) =>
                  onFractionalDetailChange?.(event.target.checked)
                }
                disabled={isProFeatureDisabled}
              />
              Add quarter/half stitches for extra detail
            </label>
            <p className="hint">
              Samples each stitch at 2× resolution and fills the matching
              fractional quadrants so diagonals (½) and quarter stitches are
              created automatically during conversion.
            </p>
            <div style={{ opacity: fractionalDetailEnabled && !isProFeatureDisabled ? 1 : 0.5 }}>
              <label style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                Intensity:
                <strong>
                  {
                    fractionalIntensityOptions.find((opt) => opt.value === safeFractionalValue)
                      ?.label || "Medium"
                  }
                </strong>
              </label>
              <input
                type="range"
                min="0"
                max={sliderMax}
                step="1"
                value={safeFractionalValue}
                onChange={(event) =>
                  onFractionalIntensityChange?.(parseInt(event.target.value, 10))
                }
                disabled={!fractionalDetailEnabled || isProFeatureDisabled}
                style={{ width: "100%" }}
              />
              <div className="hint" style={{ marginTop: "4px" }}>
                Low intensity only keeps dramatic fractional areas, while Very High
                preserves nearly all subtle diagonal detail.
              </div>
            </div>
          </div>

          <hr />

          <div className="setting-group">
            <button
              className={`btn ${
                isPaletteEmpty || isProFeatureDisabled ? "btn" : "btn-primary"
              }`}
              onClick={onConvertToStitches}
              disabled={isPaletteEmpty || isProFeatureDisabled}
              title={isProFeatureDisabled ? "Pro feature" : undefined}
              style={{ width: "100%", marginBottom: "10px" }}
            >
              Convert to Stitches
            </button>
            {isPaletteEmpty && (
              <p className="hint danger">
                Your palette is empty. Add colors first to enable conversion!
              </p>
            )}
            {isProFeatureDisabled && (
              <p className="hint danger">
                Pro Feature: Sign in and upgrade to convert images directly into
                stitches.
              </p>
            )}
            <p className="hint">
              The image will be resampled to {cols}x{rows} and converted using
              Floyd-Steinberg dithering for better detail (Active Layer will be
              overwritten).
            </p>
          </div>

          <div className="setting-group">
            <button
              className="btn"
              onClick={onUseOverlay}
              disabled={isProFeatureDisabled}
              title={isProFeatureDisabled ? "Pro feature" : undefined}
              style={{ width: "100%", marginBottom: "10px" }}
            >
              Use as Semi-Transparent Overlay
            </button>
            {isProFeatureDisabled && (
              <p className="hint danger">
                Pro Feature: Sign in and upgrade to use custom images as overlays.
              </p>
            )}
            <p className="hint">
              The image will be placed beneath your stitches as a guide. You can
              adjust its size, position, and opacity in the Image Overlay
              controls.
            </p>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
